export const settings = {
  SettingsSection: {
    extends: 'SettingsSection',
    Title: {
      text: '{{ settings.platform.title | polyglot }}'
    },
    Grid: {
      '@tabletM': {
        columns: 'repeat(3, 1fr)'
      },
      children: [
        {
          extends: 'SelectDropdownWithTitle',
          ignoreChildExtend: true,
          // Row-separator-as-chrome (design-direction.md §8 / DESIGN_LANGUAGE
          // §2.5) — was a top border split across '@dark'/'@light' with raw
          // `--color-line-dark`/`--color-line-light` custom-property names,
          // not even wrapped in a token. The row's own top padding already
          // separates it from the grid above.
          padding: 'B1 - -',
          Title: {
            opacity: '.65',
            // Was a raw `gray7` hue — a single fixed value with no light/dark
            // pair (Rule 69), composited with this row's `.65` opacity it
            // measured 2.6:1 light / 2.09:1 dark (needs 4.5; AA walk
            // 2026-08-09, LMC-4). `caption` is the adaptive neutral pair used
            // for this same de-emphasized-label role elsewhere on this page
            // (SwitchFieldWithCaption.Caption) — verified >=4.6 light /
            // >=5.7 dark at this exact opacity.
            color: 'caption',
            text: '{{ settings.platform.defaultTheme | polyglot }}'
          },
          SelectDropdown: {
            Value: {
              Text: {
                text: (el) => el.getRootState('globalTheme')
              }
            },
            Dropdown: {
              top: '121%',
              options: [
                {
                  key: 'auto',
                  labelKey: 'settings.platform.theme.auto'
                },
                {
                  key: 'dark',
                  labelKey: 'settings.platform.theme.dark'
                },
                {
                  key: 'light',
                  labelKey: 'settings.platform.theme.light'
                }
              ],
              onChoose: (ev, el, s, ctx, calleeElement) => {
                const theme = calleeElement.state.key
                el.getRootState().update(
                  {
                    globalTheme: theme,
                    sceneTheme: theme
                  },
                  {
                    message: `Updated global theme to ${theme}`
                  }
                )
                el.getRoot().call('setCookie', 'sceneTheme', theme)
                el.getRoot().call('setCookie', 'globalTheme', theme)
              },
              DropdownHeader: {
                text: '{{ settings.platform.globalTheme | polyglot }}'
              },
              ListInDropdown: {
                childProps: {
                  extends: 'KeyValueColumnFields',
                  isActive: (el, s) => el.getRootState('globalTheme') === s.key,
                  // NO `text:` here. KeyValueColumnFields renders the option
                  // label in its own `Text` slot (`s.text || s.value || s.key`)
                  // and the row HAS children, so a `text:` on the row appends a
                  // SECOND text node beside them — every option rendered its
                  // label twice with the checkmark between ("Auto ✓ Auto").
                  // Same fix as components/LanguageSelectField.js.
                  fontWeight: '400',
                  // The options carry polyglot KEYS (labelKey): a '{{ … | polyglot }}'
                  // string read out of row state is data to the template resolver since
                  // smbls 3c416df0c, and this Text slot painted raw 'Auto / Dark / Light'.
                  Text: { text: (el, s) => el.call('polyglot', s.labelKey) }
                }
              }
            }
          }
        },
        // Interface language — the person's OWN choice, next to the theme row.
        //
        // THE shared control (components/LanguageSelectField.js). The option
        // list, the endonym labelling, the `root.lang` read and the `setLang`
        // write all live there ONCE, so this row, the account page's Language
        // card and the workspace-admin default cannot drift apart.
        //
        // Only the grid-specific chrome and this row's own labels sit here.
        {
          extends: 'InterfaceLanguageField',
          ignoreChildExtend: true,
          padding: 'B1 - -',
          Title: { text: '{{ settings.language.title | polyglot }}' },
          SelectDropdown: {
            Dropdown: {
              DropdownHeader: { text: '{{ settings.language.title | polyglot }}' }
            }
          }
        },
        {
          key: 'optimizedMode',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.lowPerformance')
            }
          },
          Caption: {
            text: '{{ settings.platform.lowPerformance | polyglot }}'
          },
          onChange: (ev, el) => {
            const val = el.getUserSettings('optimizedMode')
            el.setAllUserSettings(
              {
                optimizedMode: !val,
                debuggingMode: !val,
                thumbnailPreview: !val,
                openOnDblClick: !val,
                explorerAutoscroll: val,
                allowFilesidebarPreview: val,
                allowTabs: val,
                allowTabPreview: val,
                showGrid: val,
                useGlassmorphism: val,
                allowZoombar: val,
                allowTeamCursors: val,
                showReferences: val,
                allowAutoPosition: val,
                useAnimations: val
              },
              {
                message: 'Reset settings'
              }
            )
          }
        },
        {
          key: 'explorer',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.enableExplorer')
            }
          },
          Caption: {
            text: '{{ settings.platform.enableExplorer | polyglot }}'
          }
        },
        {
          key: 'debuggingMode',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.debugging')
            }
          },
          Caption: {
            text: '{{ settings.platform.debugging | polyglot }}'
          }
        },
        {
          key: 'useDomql3',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.domql3')
            }
          },
          Caption: {
            text: '{{ settings.platform.domql3 | polyglot }}'
          }
        },
        {
          key: 'useGlassmorphism',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.glassSidebars')
            }
          },
          Caption: {
            text: '{{ settings.platform.glassSidebars | polyglot }}'
          }
        },
        {
          key: 'allowFilesidebarPreview',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.filePreviews')
            }
          },
          Caption: {
            text: '{{ settings.platform.filePreviews | polyglot }}'
          }
        },
        {
          key: 'explorerAutoscroll',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.scrollFilesSidebar')
            }
          },
          Caption: {
            text: '{{ settings.platform.scrollFilesSidebar | polyglot }}'
          }
        }
      ]
    }
  },
  CanvasSettings: {
    extends: 'SettingsSection',
    Title: {
      text: '{{ settings.canvas.title | polyglot }}',
      QuestionMarkTooltip: {
        onClick: (ev, el) => {
          el.setAllUserSettings(
            {
              optimizedMode: false,
              thumbnailPreview: false,
              showGrid: true,
              allowTabs: true,
              allowZoombar: true,
              allowAutoPosition: true,
              allowTabPreview: true,
              useAnimations: true
            },
            {
              message: 'Reset settings'
            }
          )
        }
      }
    },
    Grid: {
      '@tabletM': {
        columns: 'repeat(3, 1fr)'
      },
      children: [
        {
          key: 'thumbnailPreview',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.imagePreviews')
            }
          },
          Caption: {
            text: '{{ settings.canvas.imagePreviews | polyglot }}'
          }
        },
        {
          key: 'allowTeamCursors',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.teamCursors')
            }
          },
          Caption: {
            text: '{{ settings.canvas.teamCursors | polyglot }}'
          }
        },
        {
          key: 'showReferences',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.componentReferences')
            }
          },
          Caption: {
            text: '{{ settings.canvas.componentReferences | polyglot }}'
          }
        },
        {
          key: 'allowCanvasReplaceRouter',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.linkScroll')
            }
          },
          Caption: {
            text: '{{ settings.canvas.linkScroll | polyglot }}'
          }
        },
        {
          key: 'openOnDblClick',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.doubleClickOpen')
            }
          },
          Caption: {
            text: '{{ settings.canvas.doubleClickOpen | polyglot }}'
          }
        },
        {
          key: 'autoSelectSidebar',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.autoSelectSidebar')
            }
          },
          Caption: {
            // Was 'Double click to open:' — copy-pasted from the
            // openOnDblClick row above (SETTINGS-IA-1); this toggle is
            // autoSelectSidebar, an unrelated control.
            text: '{{ settings.canvas.autoSelectSidebar | polyglot }}'
          }
        },
        {
          key: 'showGrid',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.showGrid')
            }
          },
          Caption: {
            text: '{{ settings.canvas.showGrid | polyglot }}'
          }
        },
        {
          key: 'allowTitleOnArtboard',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.artboardTitles')
            }
          },
          Caption: {
            text: '{{ settings.canvas.artboardTitles | polyglot }}'
          }
        },
        {
          key: 'allowZoombar',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.zoomBar')
            }
          },
          Caption: {
            text: '{{ settings.canvas.zoomBar | polyglot }}'
          }
        },
        {
          key: 'allowAutoPosition',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.autoPositioning')
            }
          },
          Caption: {
            text: '{{ settings.canvas.autoPositioning | polyglot }}'
          }
        },
        {
          key: 'allowInitialSelection',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.autoInitialSelection')
            }
          },
          Caption: {
            text: '{{ settings.canvas.autoInitialSelection | polyglot }}'
          }
        },
        {
          key: 'useAnimations',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.animations')
            }
          },
          Caption: {
            text: '{{ settings.canvas.animations | polyglot }}'
          }
        },
        {
          key: 'autoCleanConsole',
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.autoCleanConsole')
            }
          },
          Caption: {
            text: '{{ settings.canvas.autoCleanConsole | polyglot }}'
          }
        }
      ]
    }
  },
  DesignSystemOptions: {},
  SyncingSettings: {
    extends: 'SettingsSection',
    state: 'settings',
    Title: {
      text: '{{ settings.sync.title | polyglot }}'
    },
    Grid: {
      '@tabletM': {
        columns: 'repeat(3, 1fr)'
      },
      children: [
        {
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.syncNavigation')
            }
          },
          onChange: (ev, el) => {
            el.toggleUserSettings('presentMode')
          },
          Caption: {
            text: '{{ settings.sync.navigation | polyglot }}'
          },
          Input: {
            checked: (el) => el.getUserSettings('presentMode')
          }
        },
        {
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.syncTeamNavigation')
            }
          },
          Caption: {
            text: '{{ settings.sync.teamNavigation | polyglot }}'
          }
        },
        {
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.syncTeamNavigation')
            }
          },
          Caption: {
            text: '{{ settings.sync.forceReload | polyglot }}'
          },
          Input: null,
          SwitchField: null,
          IconButton: {
            order: 5,
            icon: 'reload',
            onClick: (ev, el) => {
              const agreed = window.confirm(el.call('polyglot', 'settings.sync.forceReloadConfirm'))
              if (agreed) {
                el.getRootState().update(
                  {},
                  {
                    forceReload: true,
                    preventShModalUpdate: true
                  }
                )
              }
            }
          }
        },
        {
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.syncTeamNavigation')
            }
          },
          Caption: {
            text: '{{ settings.sync.forceNavigate | polyglot }}'
          },
          Input: null,
          SwitchField: null,
          IconButton: {
            order: 5,
            icon: 'arrowUp',
            onClick: (ev, el) => {
              const url = window.prompt(
                el.call('polyglot', 'settings.sync.forceNavigatePrompt'),
                window.location.pathname
              )
              el.getRootState().update(
                {},
                {
                  forceNavigate: url,
                  preventShModalUpdate: true
                }
              )
            }
          }
        }
      ]
    }
  },
  WebsiteSettings: {
    extends: 'SettingsSection',
    state: 'settings',
    Title: {
      text: '{{ settings.website.title | polyglot }}'
    },
    Grid: {
      '@tabletM': {
        columns: 'repeat(3, 1fr)'
      },
      children: [
        {
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.fetchData')
            }
          },
          Caption: {
            text: '{{ settings.website.fetchData | polyglot }}'
          }
        },
        {
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.asyncLoading')
            }
          },
          Caption: {
            text: '{{ settings.website.asyncLoading | polyglot }}'
          }
        },
        {
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.allowInspecting')
            }
          },
          Caption: {
            text: '{{ settings.website.allowInspecting | polyglot }}'
          }
        },
        {
          QuestionMarkTooltip: {
            TooltipHidden: {
              shapeDirection: 'top',
              description: (el) => el.call('polyglot', 'settings.tip.liveSync')
            }
          },
          Caption: {
            text: '{{ settings.website.liveSync | polyglot }}'
          }
        }
      ]
    }
  },
  CodeEditor: {
    extends: 'Flex',
    flow: 'y',
    state: 'settings/codeEditor',
    SectionTitle: {
      text: '{{ settings.codeEditor.title | polyglot }}',
      margin: '- - A2'
    },
    Flex: {
      gap: 'C1',
      // SIZE-1 item 2 (tickets/sonnet.md, WAVE-G, 2026-08-14/15): both
      // direct children below (the fields-panel Flex + CodePreviewWidget)
      // are `flex: 1` via this SAME childProps — the textbook flex-overflow
      // trap: a flex child's default `min-width: auto` lets its own
      // intrinsic content (an unwrapped long line in Monaco, per
      // CodePreviewWidget's `wordWrap: 'off'` default) dictate a width
      // wider than its flex-basis, which is what was producing the
      // horizontal scrollbar / off-viewport Input measured on this page.
      // `minWidth: '0'` here fixes BOTH children in one place instead of
      // duplicating the token twice — same abstraction childProps already
      // uses for `flex: 1`.
      childProps: {
        flex: 1,
        minWidth: '0'
      },
      Flex: {
        props: {
          flow: 'y',
          gap: 'B1'
        },
        childExtend: {
          extend: 'Grid',
          props: {
            gap: 'B1 C3',
            templateColumns: 'repeat(2, 1fr)'
          }
        },
        inputs: {
          childExtend: {
            extend: 'InputField',
            Input: {
              onInput: (ev, el, s) => {
                el.call('setCookie', codeEditor)
                // el.sdk.set(['settings', ''], (el.props.isNumber ? parseInt : v => v)(
                //       el.node.value
                //     ), {
                //       forceMonacoUpdate: true
                //     })
                // )
              }
            }
          },
          fontFamily: {
            Title: {
              text: '{{ settings.codeEditor.fontFamily | polyglot }}'
            },
            Input: {
              placeholder: (el) => el.call('polyglot', 'settings.codeEditor.fontFamilyPlaceholder'),
              value: '{{ fontFamily }}'
            }
          },
          fontSize: {
            Title: {
              text: '{{ settings.codeEditor.fontSize | polyglot }}'
            },
            Input: {
              placeholder: '14',
              value: '{{ fontSize }}'
            }
          },
          lineHeight: {
            Title: {
              text: '{{ settings.codeEditor.lineHeight | polyglot }}'
            },
            Input: {
              placeholder: '20',
              value: '{{ lineHeight }}'
            }
          },
          editorDebounce: {
            Title: {
              text: '{{ settings.codeEditor.debounce | polyglot }}'
            },
            Input: {
              placeholder: '20',
              value: (el, s) => s.editorDebounce || 250
            }
          }
        },
        selects: {
          childExtend: {
            extend: 'SelectDropdownWithTitle',
            Title: {
              text: (el, s) => s[el.parent.key]
            },
            SelectDropdown: {
              Dropdown: {
                top: '121%',
                onChoose: (ev, el, s, ctx, calleeElement) => {
                  const { key } = el.parent.parent
                  s.update(
                    {
                      [key]: calleeElement.state.value
                    },
                    {
                      message: `Updated ${key} settings`,
                      forceMonacoUpdate: true
                    }
                  )
                },
                ListInDropdown: {
                  childExtends: {
                    extend: 'KeyValueColumnFields',
                    props: (el, s) => ({
                      isActive:
                        el.getRootState('settings').codeEditor?.[
                          el.parent.parent.parent.parent.key
                        ] === s.value,
                      text: s.value,
                      fontWeight: '400'
                    })
                  }
                }
              }
            }
          },
          tabSize: {
            props: {},
            Title: {
              text: '{{ settings.codeEditor.tabSize | polyglot }}'
            },
            SelectDropdown: {
              Value: {
                Text: {
                  text: ({ state }) => state.tabSize || 2
                }
              },
              Dropdown: {
                options: [2, 4, 6, 8],
                DropdownHeader: {
                  text: '{{ settings.codeEditor.tabSize | polyglot }}'
                }
              }
            }
          },
          wordWrap: {
            props: {},
            Title: {
              text: '{{ settings.codeEditor.wordWrap | polyglot }}'
            },
            SelectDropdown: {
              Value: {
                Text: {
                  text: ({ state }) => state.wordWrap || 'off'
                }
              },
              Dropdown: {
                options: ['off', 'on', 'wordWrapColumn', 'bounded'],
                DropdownHeader: {
                  text: '{{ settings.codeEditor.wordWrap | polyglot }}'
                }
              }
            }
          }
        },
        toggles: {
          childExtend: {
            extend: 'SwitchFieldWithCaption',
            props: {
              cursor: 'pointer',
              // Row-separator-as-chrome (design-direction.md §8 /
              // DESIGN_LANGUAGE §2.5) — was a top border split across
              // '@dark'/'@light' with raw `--color-line-dark`/
              // `--color-line-light` custom-property names, not even
              // wrapped in a token. Top padding below already separates
              // each generated toggle row.
              padding: 'B1 - -',
              onChange: (ev, el, s) => {
                const { key } = el
                const val = s[key]
                s.update(
                  {
                    [key]: !val
                  },
                  {
                    message: `Updated ${key} settings`,
                    forceMonacoUpdate: true
                  }
                )
              }
            },
            Input: {
              checked: ({ parent, state }) => state[parent.key]
            },
            Caption: {},
            SwitchField: {}
          },
          fontLigatures: {
            props: {
              QuestionMarkTooltip: {
                TooltipHidden: {
                  shapeDirection: 'top',
                  description: (el) => el.call('polyglot', 'settings.tip.fetchData')
                }
              },
              Caption: {
                text: '{{ settings.codeEditor.fontLigature | polyglot }}'
              }
            }
          },
          'minimap.enabled': {
            props: {
              QuestionMarkTooltip: {
                TooltipHidden: {
                  shapeDirection: 'top',
                  description: (el) => el.call('polyglot', 'settings.tip.fetchData')
                }
              },
              Caption: {
                text: '{{ settings.codeEditor.minimap | polyglot }}'
              }
            }
          },
          folding: {
            props: {
              QuestionMarkTooltip: {
                TooltipHidden: {
                  shapeDirection: 'top',
                  description: (el) => el.call('polyglot', 'settings.tip.fetchData')
                }
              },
              Caption: {
                text: '{{ settings.codeEditor.folding | polyglot }}'
              }
            }
          }
        }
      },
      CodePreviewWidget: {
        // SIZE-1 item 2: `widthRange: null` used to sit here with no
        // explaining comment, unlike every other deliberate override in
        // this file — nulling it strips CodePreviewWidget's own default
        // responsive width clamp (`widthRange: 'H1'`,
        // packages/monaco/components/CodePreviewWidget.js), which only
        // compounds the flex-overflow trap fixed above. Removed so this
        // panel falls back to its own sensible default.
        props: (el, s) => ({
          Monaco: {
            foldLevel: null,
            fileTab: {
              code: el.stringifyCode(s.parse()),
              filename: 'codeEditor.js'
            },
            onCodeEditCallback: async (editor) => {
              const codeEditor = await el.call('parseStringCode', /* N5: parse the buffer, never eval it */ editor.getValue())
              el.getRootState('settings').codeEditor = codeEditor
              s.set(codeEditor, {
                message: 'Updated cope editor preferences'
              })
            }
          }
        })
      }
    },
    IconText: {
      extends: 'DocsLink',
      margin: 'B1 - -',
      color: 'inactive',
      alignSelf: 'end',
      cursor: 'pointer',
      gap: 'X2',
      target: 'blank',
      href: 'https://microsoft.github.io/monaco-editor/typedoc/variables/editor.EditorOptions.html',
      icon: 'questionMarkFill',
      text: '{{ settings.codeEditor.monacoOptions | polyglot }}',
      fontWeight: 400
    }
  },
  SettingsFooter: {},
  extend: ['Page', 'Flex'],
  props: {
    flow: 'y',
    gap: 'D1'
  }
}
